import {
  HOUSEHOLD_DOC,
  HOUSEHOLD_MEMBERS_KEY,
  MEMBER_COLORS,
  type HouseholdMember,
} from '@awwyeah/shared';
import type { CSSProperties } from 'react';
import { useYDoc, useYMap } from '../../sync';
import { useUser } from '../auth';
import { memberTint, textOnMemberColor } from '../../theme/memberColor';

/**
 * Who's who for the digest view — avatars on today's agenda and attribution on new list
 * items / check-ins. Same convention as `checkin/members.ts` (each feature module keeps its
 * own tiny copy rather than importing another module's file): a member's colour is their
 * own stored profile hex, applied inline — not a `--m-*` design token assigned by roster
 * position (see docs/DECISIONS.md, 2026-08-01), and this module only ever **reads** the
 * `household` doc.
 */

export interface MemberStyle {
  /** Solid fill for the avatar dot, with a legible label colour. */
  fill: CSSProperties;
  /** Text in the member's colour. */
  text: CSSProperties;
  /** Soft background for chips. */
  tint: CSSProperties;
  /** Left edge of an agenda-style row. */
  edge: CSSProperties;
}

function styleForColor(hex: string): MemberStyle {
  return {
    fill: { backgroundColor: hex, color: textOnMemberColor(hex) },
    text: { color: hex },
    tint: { backgroundColor: memberTint(hex) },
    edge: { borderLeftColor: hex },
  };
}

/** Neutral treatment for "nobody in particular" (an event with no owner, say). */
export const NEUTRAL_STYLE: MemberStyle = {
  fill: { backgroundColor: 'var(--app-fg-3)', color: 'var(--app-surface)' },
  text: { color: 'var(--app-fg-3)' },
  tint: { backgroundColor: 'var(--app-raised)' },
  edge: { borderLeftColor: 'var(--app-fg-3)' },
};

/** Deterministic fallback colour for a member id we have no profile for yet. */
function fallbackColor(id: string): string {
  let hash = 0;
  for (let index = 0; index < id.length; index += 1)
    hash = (hash * 31 + id.charCodeAt(index)) >>> 0;
  return MEMBER_COLORS[hash % MEMBER_COLORS.length] as string;
}

export function initialOf(name: string): string {
  return (name.trim()[0] ?? '?').toUpperCase();
}

export interface DigestMember {
  id: string;
  name: string;
  initial: string;
  style: MemberStyle;
  avatarEmoji?: string;
}

/** Builds the directory. Pure, so it's testable without mounting anything. */
export function buildMemberDirectory(
  roster: readonly HouseholdMember[],
  self?: { id: string; name: string; color?: string; avatarEmoji?: string } | null,
): Map<string, DigestMember> {
  const directory = new Map<string, DigestMember>();

  for (const member of roster) {
    directory.set(member.id, {
      id: member.id,
      name: member.name,
      initial: initialOf(member.name),
      style: styleForColor(member.color),
      ...(member.avatarEmoji ? { avatarEmoji: member.avatarEmoji } : {}),
    });
  }

  if (self && !directory.has(self.id)) {
    directory.set(self.id, {
      id: self.id,
      name: self.name,
      initial: initialOf(self.name),
      style: styleForColor(self.color ?? fallbackColor(self.id)),
      ...(self.avatarEmoji ? { avatarEmoji: self.avatarEmoji } : {}),
    });
  }

  return directory;
}

export function unknownMember(id: string): DigestMember {
  return { id, name: 'Someone', initial: '?', style: styleForColor(fallbackColor(id)) };
}

export interface MemberDirectory {
  get(id: string): DigestMember;
  all(): DigestMember[];
  selfId: string | null;
  selfName: string | null;
}

export function useMembers(): MemberDirectory {
  const { user } = useUser();
  const householdDoc = useYDoc(HOUSEHOLD_DOC);
  const { entries } = useYMap<HouseholdMember>(householdDoc, HOUSEHOLD_MEMBERS_KEY);

  const roster = entries.map(([, member]) => member).filter((member) => Boolean(member?.id));
  const directory = buildMemberDirectory(
    roster,
    user
      ? {
          id: user.id,
          name: user.name,
          color: user.color,
          ...(user.avatarEmoji ? { avatarEmoji: user.avatarEmoji } : {}),
        }
      : null,
  );

  return {
    get: (id) => directory.get(id) ?? unknownMember(id),
    all: () => [...directory.values()],
    selfId: user?.id ?? null,
    selfName: user?.name ?? null,
  };
}
